import { IWhoToFollow, IWhoToFollowData, IWhoToFollowAction } from 'models/whotofollow';
import { IFollowingItem, IEntityDto } from 'models/common';
import { IStore } from 'redux/IStore';
import { IUserPreferences } from 'models/user/userpreferences';
import { accessTokenIsPresent } from 'containers/AccessControl/helpers';

/** Action Types */
export const REQUEST: string = 'whotofollow/REQUEST';

export const GET_WHO_TO_FOLLOW_ITEMS_SUCCESS: string = 'whotofollow/GET_WHO_TO_FOLLOW_ITEMS_SUCCESS';
export const GET_WHO_TO_FOLLOW_ITEMS_FAILURE: string = 'whotofollow/GET_WHO_TO_FOLLOW_ITEMS_FAILURE';

export const FOLLOW_ITEM_START: string = 'whotofollow/FOLLOW_ITEM_START';
export const FOLLOW_ITEM_SUCCESS: string = 'whotofollow/FOLLOW_ITEM_SUCCESS';
export const FOLLOW_ITEM_FAILURE: string = 'whotofollow/FOLLOW_ITEM_FAILURE';

export const GET_FOLLOWING_FAILURE: string = 'whotofollow/GET_FOLLOWING_FAILURE';

/** Initial State */
const initialState: IWhoToFollowData = {
	items: [],
};

/** Reducer */
export function whoToFollowReducer(state = initialState, action: IWhoToFollowAction) {
	switch (action.type) {
		case REQUEST:
			return Object.assign({}, state, {
				isFetching: true,
				error: false,
			});

		case GET_WHO_TO_FOLLOW_ITEMS_SUCCESS:
			return Object.assign({}, state, {
				items: action.payload.items,
				isFetching: false,
			});

		case GET_WHO_TO_FOLLOW_ITEMS_FAILURE:
			return Object.assign({}, state, {
				isFetching: false,
				message: action.payload.message,
				error: true,
			});

		case FOLLOW_ITEM_FAILURE:
			return Object.assign({}, state, {
				isFetching: false,
				message: action.payload.message,
				error: true,
			});

		case FOLLOW_ITEM_START:
			return Object.assign({}, state, {
				isFetching: true,
			});

		case FOLLOW_ITEM_SUCCESS:
			return Object.assign({}, state, {
				isFetching: false,
			});

		case GET_FOLLOWING_FAILURE:
			return Object.assign({}, state, {
				isFetching: false,
				message: action.payload.message,
				error: true,
			});

		default:
			return state;
	}
}

/** Async Action Creator */
export function getWhoToFollowItems() {
	return (dispatch, getState: () => IStore) => {
		const userPreferences: IUserPreferences = getState().userPreferences;

		dispatch(request());
		return fetch(
			getState().fetchDefault.baseAPI + 'api/user/whotofollow',
			Object.assign({}, getState().fetchDefault.requestInit),
		)
			.then((res) => {
				if (res.ok) {
					return res
						.json()
						.then((res) => dispatch(getWhoToFollowItemsSuccess(res)))
						.then(() => {
							if (userPreferences.id !== null) {
								dispatch(getFollowing());
							} else {
								dispatch(updateItemsWithFollow(null));
							}
						});
				} else {
					const status: number = res.status;
					const statusText: string = res.statusText;
					return res
						.json()
						.then((res) => {
							res.status = status;
							res.statusText = statusText;
							dispatch(getWhoToFollowItemsFailure(res));
						})
						.catch(() => {
							const message: any = { status, statusText };
							dispatch(getWhoToFollowItemsFailure(message));
						});
				}
			})
			.catch((err) => {
				dispatch(getWhoToFollowItemsFailure(err));
			});
	};
}

/** Action Creator */
export function followItem(itemType: string, itemId: string) {
	return (dispatch, getState: () => IStore) => {
		dispatch(request());
		dispatch(followItemStart());

		return fetch(
			getState().fetchDefault.baseAPI + 'api/' + itemType + '/' + itemId + '/Follow',
			Object.assign({}, getState().fetchDefault.requestInit, {
				method: 'POST',
			}),
		)
			.then((res) => {
				if (res.ok || res.status === 409) {
					dispatch(followItemSuccess());
					dispatch(updateFollowedItem(itemId));
				} else {
					const status: number = res.status;
					const statusText: string = res.statusText;
					return res
						.json()
						.then((res) => {
							res.status = status;
							res.statusText = statusText;
							dispatch(followItemFailure(res));
						})
						.catch(() => {
							const message: any = { status, statusText };
							dispatch(followItemFailure(message));
						});
				}
			})
			.catch((err) => {
				dispatch(followItemFailure(err));
			});
	};
}

/** Action Creator */
export function updateFollowedItem(id: string) {
	return (dispatch, getState: () => IStore) => {
		const items: IWhoToFollow[] = getState().whoToFollow.items;
		const itemInList: IWhoToFollow = items.filter((x) => JSON.parse(x.entityId).id === id)[0];
		const itemIndex = items.indexOf(itemInList);
		items[itemIndex].following = true;

		dispatch(getWhoToFollowItemsSuccess(items));
	};
}

/** Action Creator */
export function getFollowing() {
	return (dispatch, getState: () => IStore) => {
		if (!accessTokenIsPresent()) {
			return Promise.resolve(false);
		}
		dispatch(request());

		const itemsToGet: IEntityDto[] = getState().whoToFollow.items.map((item) => {
			return {
				entityId: item.entityId,
			};
		});

		return fetch(
			getState().fetchDefault.baseAPI + 'api/User/Following',
			Object.assign({}, getState().fetchDefault.requestInit, {
				method: 'POST',
				body: JSON.stringify(itemsToGet),
			}),
		)
			.then((res) => {
				if (res.ok) {
					return res.json().then((res) => dispatch(updateItemsWithFollow(res)));
				} else {
					const status: number = res.status;
					const statusText: string = res.statusText;
					return res
						.json()
						.then((res) => {
							res.status = status;
							res.statusText = statusText;
							dispatch(getFollowingFailure(res));
						})
						.catch(() => {
							const message: any = { status, statusText };
							dispatch(getFollowingFailure(message));
						});
				}
			})
			.catch((err) => {
				dispatch(getFollowingFailure(err));
			});
	};
}

/** Action Creator */
export function updateItemsWithFollow(followingItems?: IFollowingItem[]) {
	return (dispatch, getState: () => IStore) => {
		const items: IWhoToFollow[] = getState().whoToFollow.items;

		if (followingItems !== null) {
			followingItems.map((followingItem: IFollowingItem) => {
				const item: IWhoToFollow = items.filter((i) => i.entityId === followingItem.key.entityId)[0];
				const itemIndex = items.indexOf(item);
				item.following = followingItem.value;
				items[itemIndex] = item;
			});
		} else {
			items.map((item) => {
				item.following = false;
			});
		}

		dispatch(getWhoToFollowItemsSuccess(items));
	};
}

/** Action Creator */
export function request(): IWhoToFollowAction {
	return {
		type: REQUEST,
	};
}

/** Action Creator */
export function getWhoToFollowItemsSuccess(data: IWhoToFollow[]): IWhoToFollowAction {
	return {
		type: GET_WHO_TO_FOLLOW_ITEMS_SUCCESS,
		payload: {
			items: data,
		},
	};
}

/** Action Creator */
export function getWhoToFollowItemsFailure(message: any): IWhoToFollowAction {
	return {
		type: GET_WHO_TO_FOLLOW_ITEMS_FAILURE,
		payload: {
			message,
		},
	};
}

/** Action Creator */
export function followItemStart(): IWhoToFollowAction {
	return {
		type: FOLLOW_ITEM_START,
	};
}

/** Action Creator */
export function followItemSuccess(): IWhoToFollowAction {
	return {
		type: FOLLOW_ITEM_SUCCESS,
	};
}

/** Action Creator */
export function followItemFailure(message: any): IWhoToFollowAction {
	return {
		type: FOLLOW_ITEM_FAILURE,
		payload: {
			message,
		},
	};
}

/** Action Creator */
export function getFollowingFailure(message: any): IWhoToFollowAction {
	return {
		type: GET_FOLLOWING_FAILURE,
		payload: {
			message,
		},
	};
}
